/**
 * Reading a CRM refusal. Pure (no transport), so it is NOT part of the mocked `crmApi`
 * module: screens under test branch on the real envelope logic.
 *
 * Errors travel as `{"detail": {"code": "UPPER_SNAKE", ...extra}}` (contract C0.02).
 * `apiErrorCode` reads the code; `crmErrorDetail` reads the extra fields a few refusals
 * carry (`CLIENT_EXISTS {client_id}`); `crmErrorMessage` turns any failure into one
 * localised sentence.
 */
// Relative on purpose: lib/apiErrors.js is pure and resolves for real under Jest.
import { apiErrorCode, extractApiError, knownApiErrorMessage } from "../../../lib/apiErrors";

export { apiErrorCode };

/** CRM refusal code → its sentence in the `crm` namespace. */
export const CRM_ERROR_KEYS = Object.freeze({
  CLIENT_EXISTS: "errors.clientExists",
  NUDGE_CLOSED: "errors.nudgeClosed",
  ENCOURAGE_MESSAGE_INVALID: "errors.encourageMessageInvalid",
  CLIENT_NOT_FOUND: "errors.clientNotFound",
  EMAIL_INVALID: "errors.emailInvalid",
  CLIENT_NOT_CONSENTED: "errors.clientNotConsented",
  DAILY_SEND_CAP_REACHED: "errors.dailySendCap",
  VIEW_NAME_TAKEN: "errors.viewNameTaken",
  BULK_TOO_LARGE: "errors.bulkTooLarge",
  CONFIRM_NAME_MISMATCH: "errors.confirmNameMismatch",
  DELETE_COUNT_MISMATCH: "errors.deleteCountMismatch",
  IMPORT_TOO_MANY_ROWS: "errors.importTooManyRows",
  IMPORT_EMPTY: "errors.importEmpty",
  IMPORT_EMAIL_UNMAPPED: "errors.importEmailUnmapped",
  IMPORT_FILE_TOO_LARGE: "errors.importFileTooLarge",
  IMPORT_UNREADABLE: "errors.importUnreadable",
  IMPORT_NOT_MAPPED: "errors.importNotMapped",
  IMPORT_NOT_COMPLETED: "errors.importNotCompleted",
  IMPORT_MAPPING_INVALID: "errors.importMappingInvalid",
  REMINDER_SEND_UNAVAILABLE: "errors.reminderSendUnavailable",
  PACK_BALANCE_NOT_FOUND: "errors.packBalanceNotFound",
  ATTENDANCE_TOO_EARLY: "errors.attendanceTooEarly",
  ATTENDANCE_NOT_APPLICABLE: "errors.attendanceNotApplicable",
  ATTENDANCE_LOCKED: "errors.attendanceLocked",
  REMINDER_NOT_APPLICABLE: "errors.reminderNotApplicable",
  TIME_UNAVAILABLE: "errors.timeUnavailable",
  SERVICE_NOT_BOOKABLE_BY_ADMIN: "errors.serviceNotBookable",
  PACK_EXHAUSTED: "errors.packExhausted",
  PACK_NOT_FOR_SERVICE: "errors.packNotForService",
  PACK_EXPIRED: "errors.packExpired",
  BOOKING_NOT_FOR_CLIENT: "errors.bookingNotForClient",
  CRM_OWNER_ONLY: "errors.ownerOnly",
  CRM_ENCRYPTION_UNAVAILABLE: "errors.encryptionUnavailable",
  // crm-module BEGIN C2.05 (lane F1)
  SYSTEM_TRIGGER_LOCKED: "errors.systemTriggerLocked",
  SYSTEM_SEQUENCE: "errors.systemSequence",
  MERGE_FIELD_UNKNOWN: "errors.mergeFieldUnknown",
  OFFSET_ANCHOR_INVALID: "errors.offsetAnchorInvalid",
  SEQUENCE_EMPTY: "errors.sequenceEmpty",
  // Pathways phase 3: a trigger (type or params) the automation schema refuses (422,
  // `field` names the part: trigger | trigger.type | trigger.params | trigger.params.<key>).
  TRIGGER_INVALID: "errors.triggerInvalid",
  AUDIENCE_CHANGED: "errors.audienceChanged",
  DOMAIN_INVALID: "errors.domainInvalid",
  DOMAIN_IN_USE: "errors.domainInUse",
  RESEND_UNAVAILABLE: "errors.resendUnavailable",
  // crm-module END
  // crm-module BEGIN C2.03/C2.04 (lane F2)
  SERVICE_PER_MINUTE: "errors.servicePerMinute",
  PACK_BALANCE_OUT_OF_RANGE: "errors.packBalanceOutOfRange",
  ORIGIN_NOT_ALLOWED: "errors.originNotAllowed",
  // crm-module END
  // crm-module BEGIN C3.02 — Team refusals (lane F5).
  MEMBER_EXISTS: "errors.memberExists",
  CANNOT_INVITE_OWNER: "errors.cannotInviteOwner",
  INVITE_EMAIL_MISMATCH: "errors.inviteEmailMismatch",
  INVITE_EXPIRED: "errors.inviteExpired",
  // crm-module END
  // crm-module BEGIN C3.02/C3.04 — Promote refusals (lane F4).
  PROMOTION_ALREADY_ACTIVE: "errors.promotionAlreadyActive",
  // BOOSTER_REQUIRED (402) is not shown as a generic toast: PromoteModal branches on the
  // code first and routes to the booster checkout. This entry is the fallback for the
  // rare path where it surfaces somewhere that only calls `crmErrorMessage`.
  BOOSTER_REQUIRED: "errors.boosterRequired",
  MEDIA_NOT_FOUND: "errors.mediaNotFound",
  DOCUMENT_NOT_FOUND: "errors.documentNotFound",
  UNSUPPORTED_FILE_TYPE: "errors.unsupportedFileType",
  FILE_TOO_LARGE: "errors.fileTooLarge",
  // crm-module END
});

/** The structured extra fields of a coded refusal; `{}` when it carries none. */
export const crmErrorDetail = (e) => {
  const detail = e?.response?.data?.detail;
  return detail && typeof detail === "object" && !Array.isArray(detail) ? detail : {};
};

/**
 * One localised sentence for any failure.
 *
 *   a CRM code        → its `crm` sentence
 *   a shared code     → lib/apiErrors' sentence (plan limit, capability, …)
 *   a 422 field list  → lib/apiErrors' flattening, in the common namespace
 *   anything else     → the caller's fallback — never a JSON dump of an unknown
 *                       structured `detail`, never axios' English "Network Error"
 *
 * @param {unknown} e
 * @param {Function} t `useT("crm")`
 * @param {Function} tc `useT("common")`
 * @param {string} [fallbackKey] a `crm` key
 */
export const crmErrorMessage = (e, t, tc, fallbackKey = "errors.saveFailed") => {
  const code = apiErrorCode(e);
  const key = CRM_ERROR_KEYS[code];
  if (key) return t(key);
  const shared = knownApiErrorMessage(e, tc);
  if (shared !== null) return shared;
  const detail = e?.response?.data?.detail;
  if (Array.isArray(detail)) return extractApiError(e, t(fallbackKey), tc);
  if (typeof detail === "string" && detail.trim()) {
    const trimmed = detail.trim();
    const lower = trimmed.toLowerCase();
    if (lower.includes("unsupported file type")) {
      return t("errors.unsupportedFileType", null, trimmed);
    }
    if (lower.includes("file too large")) {
      return t("errors.fileTooLarge", null, trimmed);
    }
    // Any other string detail is a server diagnostic in English, not the reader's
    // language: the localised fallback below says it instead.
    return t(fallbackKey);
  }
  const msg = typeof detail?.message === "string"
    ? detail.message.trim()
    : (typeof e?.response?.data?.message === "string" ? e.response.data.message.trim() : null);
  if (msg) return msg;
  return t(fallbackKey);
};
